import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Clock } from "lucide-react"
import { getSavedRoutes } from "@/lib/saveRoutes"
import { ScrollArea } from "@/components/ui/scroll-area"
import { formatDistanceToNow } from "date-fns"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

interface SavedRoutesSelectorProps {
  onLoadSavedRoute?: (routeId: string) => void
  disabled?: boolean
}

export const SavedRoutesSelector: React.FC<SavedRoutesSelectorProps> = ({
  onLoadSavedRoute,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  // Get fresh routes when opening
  const savedRoutes = getSavedRoutes()

  const handleRouteSelect = (routeId: string) => {
    onLoadSavedRoute?.(routeId)
    setIsOpen(false)
  }

  // if we are not in product (!window.XMPP) or there are no saved routes, don't show the selector
  if (
    !window.XMPP ||
    window.anonymousXMPP ||
    !savedRoutes ||
    savedRoutes.length === 0
  )
    return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          aria-label={t("maps.savedRoutesSelector.load")}
        >
          <Clock className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="p-2">
          <div className="pb-2 text-sm font-medium text-muted-foreground">
            {t("maps.savedRoutesSelector.title")}
          </div>
          <ScrollArea>
            <div className="max-h-64 space-y-1">
              {savedRoutes.map((route) => (
                <div
                  key={route.id}
                  className={
                    "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-2 text-sm hover:bg-accent"
                  }
                  onClick={() => handleRouteSelect(route.id)}
                >
                  <div className="flex min-w-0 flex-1 flex-col items-start">
                    <span className="w-full truncate font-medium">
                      {route.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("maps.savedRoutesSelector.agoAndStops", {
                        ago: formatDistanceToNow(route.timestamp, {
                          addSuffix: true,
                          locale: getDateFnsLocale(),
                        }),
                        count: route.temporaryRoute.stops.length,
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  )
}
